import React, {ReactElement} from 'react';
import {toReadable} from 'shared/utils/number';
import {
  useFutureBudgetAmount,
  useFutureDailyAmount,
  useKeyboardInput,
  usePristineDetector,
} from 'ui/utils/core';
import {isIncome, useInputMode} from 'store/UserInput';

import {Shout, Text} from 'components/Text';
import {DynamicFontSize} from 'components/DynamicFontSize';
import {Section} from 'components/Section';
import {useBudgetStore} from 'ui/store/entities/Budget';
import {perDay} from 'core/LifeCycle';

function selectMainColor(
  isPristine: boolean,
  futureBudget: number,
  input: number,
  mode: UserInputMode
): string {
  if (isPristine) return 'grey';
  if (isIncome(mode) && input) return 'green';
  if (futureBudget <= 0) return 'red';

  return 'default';
}

function selectLabelColor(isPristine: boolean): string {
  return isPristine ? 'grey' : 'default';
}

function selectLabel(
  currentPerDay: number,
  futureDaily: number,
  futureBudget: number,
  input: number,
  mode: UserInputMode
): string {
  if (isIncome(mode) && input && futureDaily > 0)
    return `Новый бюджет на день. Был ${toReadable(currentPerDay)}`;
  if (!isIncome(mode)) {
    if (futureBudget <= 0) return 'Эта трата будет последней';
    if (futureDaily <= 0) return 'На сегодня. Тратим общий бюджет';
  }
  return 'На сегодня';
}

export const Counter = (): ReactElement => {
  const budget = useBudgetStore();
  const futureDaily = useFutureDailyAmount();
  const futureBudget = useFutureBudgetAmount();

  const currentPerDay = perDay(budget);

  const isPristine = usePristineDetector();
  const input = useKeyboardInput();
  const mode = useInputMode();

  const futurePerDay =
    isIncome(mode) && input ? perDay({...budget, initial: budget.initial + input}) : perDay(budget);

  const labelColor = selectLabelColor(isPristine);
  const mainColor = selectMainColor(isPristine, futureBudget, input, mode);
  const label = selectLabel(currentPerDay, futureDaily, futureBudget, input, mode);

  const displayAmount = isIncome(mode) && input && futureDaily > 0 ? futurePerDay : futureDaily;

  return (
    <Section data-test='main'>
      <Shout color={mainColor} data-test='value'>
        <DynamicFontSize>{toReadable(displayAmount)}</DynamicFontSize>
      </Shout>
      <Text color={labelColor} data-test='label'>
        {label}
      </Text>
    </Section>
  );
};
